/** Renders exact server-selected passages and scoped counts; no matching, categorisation or scoring in React. */
import type { ShopifyResultCodebook } from '@testfeed/shopify-contracts'
import { BlockHeading } from '../study-builder/SectionFrame'
import { ServerEvidencePassage } from './ServerEvidencePassage'
import { ServerResultQuote } from './ServerResultQuote'

export function ServerResultCodebook({ data }: { data: ShopifyResultCodebook }) {
  return (
    <s-stack direction="block" gap="large-200">
      <BlockHeading>{data.heading}</BlockHeading>
      <s-paragraph>{data.description}</s-paragraph>
      {data.notices.map((notice) => (
        <s-paragraph key={notice} color="subdued">
          {notice}
        </s-paragraph>
      ))}
      <details className="response-phrase-disclosure">
        <summary>{data.coverageHeading}</summary>
        <s-stack direction="block" gap="base">
          {data.coverage.map((row) => (
            <s-stack key={row.label} direction="block" gap="small">
              <s-text type="strong">{row.label}</s-text>
              <s-text color="subdued">{row.detail}</s-text>
            </s-stack>
          ))}
        </s-stack>
      </details>
      <CodebookCategories categories={data.categories} moreLabel={data.morePassagesLabel} />
      <OtherCodebookCategories data={data} />
      <s-stack direction="block" gap="small">
        <BlockHeading>{data.unreviewed.heading}</BlockHeading>
        <s-paragraph color="subdued">{data.unreviewed.detail}</s-paragraph>
      </s-stack>
    </s-stack>
  )
}

function CodebookCategories({
  categories,
  moreLabel,
}: {
  categories: ShopifyResultCodebook['categories']
  moreLabel: string
}) {
  return (
    <s-stack direction="block" gap="large-200">
      {categories.map((category) => (
        <s-stack key={category.id} direction="block" gap="base">
          <BlockHeading>{category.title}</BlockHeading>
          <s-paragraph color="subdued">{category.description}</s-paragraph>
          <details className="response-phrase-disclosure">
            <summary>{category.countsHeading}</summary>
            <s-stack direction="block" gap="base">
              {category.counts.map((count) => (
                <s-stack key={count.label} direction="block" gap="small">
                  <s-text type="strong">{count.label}</s-text>
                  <s-text color="subdued">{count.detail}</s-text>
                </s-stack>
              ))}
            </s-stack>
          </details>
          <s-text color="subdued">{category.shownLabel}</s-text>
          <CodebookPassages passages={category.passages} moreLabel={moreLabel} />
          <RelatedCodebookAnswers data={category.related} />
          <s-divider />
        </s-stack>
      ))}
    </s-stack>
  )
}

function OtherCodebookCategories({ data }: { data: ShopifyResultCodebook }) {
  if (!data.otherCategories.length) return null
  return (
    <details className="response-phrase-disclosure">
      <summary>{data.otherCategoriesHeading}</summary>
      <CodebookCategories categories={data.otherCategories} moreLabel={data.morePassagesLabel} />
    </details>
  )
}

function CodebookPassages({
  passages,
  moreLabel,
}: {
  passages: ShopifyResultCodebook['categories'][number]['passages']
  moreLabel: string
}) {
  const [first, ...remaining] = passages
  if (!first) return null
  const more = remaining.length ? (
    <details className="response-phrase-disclosure">
      <summary>{moreLabel}</summary>
      <s-stack direction="block" gap="base">
        {remaining.map((passage) => (
          <ServerEvidencePassage key={passage.id} {...passage} />
        ))}
      </s-stack>
    </details>
  ) : null
  return (
    <s-stack direction="block" gap="base">
      <ServerEvidencePassage {...first} />
      {more}
    </s-stack>
  )
}

function RelatedCodebookAnswers({ data }: { data: ShopifyResultCodebook['categories'][number]['related'] }) {
  if (!data) return null
  return (
    <details className="response-phrase-disclosure">
      <summary>{data.heading}</summary>
      <s-stack direction="block" gap="base">
        <s-paragraph>{data.description}</s-paragraph>
        <s-text color="subdued">{data.shownLabel}</s-text>
        {data.answers.map((answer) => (
          <ServerResultQuote key={answer.id} answer={answer} />
        ))}
      </s-stack>
    </details>
  )
}
